'use client'

/**
 * Side portal selector — one-click shift to DEV (or PROD / employee / labs).
 * Fixed right-edge rail; open with click or Alt+P.
 */

import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  buildPortalHref,
  detectPortalEnv,
  envBadgeLabel,
  getPrimaryDevPortal,
  isPortalHere,
  listPortals,
  type PortalDestination,
} from './portalCatalog'
import styles from './PortalSelector.module.css'

const STORAGE_KEY = 'mechagora-portal-selector-open'

function pillClass(badge: PortalDestination['badge']): string {
  switch (badge) {
    case 'DEV':
      return styles.pillDev ?? ''
    case 'PROD':
      return styles.pillProd ?? ''
    case 'LAB':
      return styles.pillLab ?? ''
    case 'EMP':
      return styles.pillEmp ?? ''
    case 'OPS':
      return styles.pillOps ?? ''
    default:
      return ''
  }
}

function envTabClass(env: ReturnType<typeof detectPortalEnv>): string {
  if (env === 'production') return styles.badgeProd ?? ''
  if (env === 'sandbox') return styles.badgeDev ?? ''
  if (env === 'local') return styles.badgeLocal ?? ''
  return styles.badgeDev ?? ''
}

function navigateTo(dest: PortalDestination) {
  const href = buildPortalHref(dest)
  if (!dest.origin) {
    window.location.assign(href)
    return
  }
  // Full navigation across hosts (session cookies are host-scoped)
  window.location.assign(href)
}

export function PortalSelector() {
  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const [host, setHost] = useState('')
  const [path, setPath] = useState('')
  const panelId = useId()

  useEffect(() => {
    setMounted(true)
    setHost(window.location.hostname)
    setPath(window.location.pathname)
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') setOpen(true)
    } catch {
      /* private mode */
    }
  }, [])

  useEffect(() => {
    if (!mounted) return
    try {
      sessionStorage.setItem(STORAGE_KEY, open ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [open, mounted])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault()
        setOpen((v) => !v)
      }
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const env = useMemo(() => detectPortalEnv(host), [host])
  const portals = useMemo(() => listPortals({ includeDevOnly: true }), [])
  const primaryDev = useMemo(() => getPrimaryDevPortal(), [])
  const onDevAlready =
    env === 'sandbox' ||
    env === 'local' ||
    isPortalHere(primaryDev, host, path)

  const openPanel = useCallback(() => setOpen(true), [])
  const closePanel = useCallback(() => setOpen(false), [])

  if (!mounted) return null

  const tab = (
    <button
      type="button"
      className={`${styles.tab} ${open ? styles.tabOpen : ''}`}
      data-portal-rail="tab"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      aria-controls={panelId}
      aria-label={`Portal selector. Current environment ${envBadgeLabel(env)}. ${open ? 'Close' : 'Open'
        }`}
    >
      <span className={`${styles.tabBadge} ${envTabClass(env)}`}>
        {envBadgeLabel(env)}
      </span>
      <span>Portals</span>
    </button>
  )

  const panel = open ? (
    <>
      <button
        type="button"
        className={styles.scrim}
        aria-label="Close portal selector"
        onClick={closePanel}
      />
      <aside
        id={panelId}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Portal selector"
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <p className={styles.kicker}>Access rail</p>
            <h2 className={styles.title}>Portal selector</h2>
            <p className={styles.sub}>
              One click to shift hosts. Sessions are per-domain — you may need to
              sign in again on the target.
            </p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={closePanel}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={onDevAlready && primaryDev.origin !== null && env === 'sandbox'}
            onClick={() => {
              if (onDevAlready && env === 'sandbox' && !primaryDev.devOnly) {
                // Already on DEV client host — still allow path reset to desk
                navigateTo({ ...primaryDev, preservePath: false, path: '/client/dashboard' })
                return
              }
              navigateTo(primaryDev)
            }}
          >
            <span className={styles.primaryLabel}>
              {env === 'sandbox' || env === 'local'
                ? 'Open client desk on DEV'
                : 'Shift to DEV access →'}
            </span>
            <span className={styles.primaryHint}>
              {primaryDev.origin}
              {primaryDev.preservePath ? ' · keeps current path' : ''}
            </span>
          </button>

          <p className={styles.sectionLabel}>All destinations</p>
          <ul className={styles.list}>
            {portals.map((dest) => {
              const here = isPortalHere(dest, host, path)
              return (
                <li key={dest.id}>
                  <button
                    type="button"
                    className={`${styles.item} ${here ? styles.itemHere : ''}`}
                    disabled={here && dest.origin === null}
                    onClick={() => {
                      if (here && dest.origin === null) return
                      navigateTo(dest)
                    }}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemLabel}>{dest.label}</span>
                      <span className={`${styles.pill} ${pillClass(dest.badge)}`}>
                        {here ? 'HERE' : dest.badge}
                      </span>
                    </div>
                    <p className={styles.itemDesc}>{dest.description}</p>
                  </button>
                </li>
              )
            })}
          </ul>

          <p className={styles.meta}>
            You are on <code>{host || '—'}</code>
            {path ? (
              <>
                {' '}
                · <code>{path}</code>
              </>
            ) : null}
          </p>
          <p className={styles.footerNote}>
            Shortcut: <kbd>Alt</kbd>+<kbd>P</kbd>. PROD is .com · DEV is .net.
            Employee and client identity stay separate.
          </p>
        </div>
      </aside>
    </>
  ) : null

  return createPortal(
    <>
      {tab}
      {panel}
    </>,
    document.body
  )
}

export default PortalSelector
